import { hideScrollbar } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

export const HEADING_ID = 'customers-reviews-preview-heading';

/** The design's card width, as the top of a clamp so a narrow layout still peeks at the next card. */
const CARD_MAX_WIDTH = '280px';
const CARD_MIN_WIDTH = '240px';
const CARD_COUNT = 4;

/**
 * From this width the four cards share the row instead of scrolling: four at their
 * minimum plus three gaps (4 x 240 + 3 x 12). Below it the row scrolls and the fourth
 * card is cut off at the edge, which is what says there is more.
 */
const FOUR_ACROSS_FROM = '1004px';

/** The container width from which the "view all" button stops spanning the row. */
const BUTTON_OWN_WIDTH_FROM = '768px';

export const root = css`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.md});
  font-family: var(${themeVariables.typography.body.fontFamily});
  color: var(${themeVariables.colors.content.default});
`;

export const header = css`
  display: flex;
  align-items: center;
`;

/**
 * The column stretches the button to the row's width; from a tablet's width it takes its
 * own. Not the button's `fullWidth` prop, which cannot follow the container.
 */
export const footer = css`
  display: flex;
  flex-direction: column;

  @container (min-width: ${BUTTON_OWN_WIDTH_FROM}) {
    flex-direction: row;
    justify-content: flex-start;
  }
`;

export const heading = css`
  margin: 0;
  font-family: var(${themeVariables.typography.heading.fontFamily});
  font-weight: var(${themeVariables.typography.heading.fontWeight});
  font-size: var(${themeVariables.typography.heading.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.heading.sizes.sm.lineHeight});
  letter-spacing: var(${themeVariables.typography.heading.letterSpacing});
`;

export const track = css`
  display: flex;
  overflow-x: auto;
  column-gap: var(${themeVariables.spacing.sm});
  margin: 0;
  padding: 0;
  list-style: none;
  scroll-snap-type: x mandatory;
  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }
  ${hideScrollbar}
`;

/**
 * `cqi` resolves against the section root's `container-type: inline-size`, so the width
 * holds wherever the section sits; a percentage flex-basis in a scroll container did not.
 * `min-width: 0` is load-bearing: a card's one-line reply row is wider than 280px and
 * would otherwise push the item past the width rule. Once four fit, they share the row.
 */
const cardWidth = css`
  flex: 0 0 auto;
  width: clamp(${CARD_MIN_WIDTH}, 78cqi, ${CARD_MAX_WIDTH});
  min-width: 0;

  @container (min-width: ${FOUR_ACROSS_FROM}) {
    width: calc((100cqi - ${CARD_COUNT - 1} * var(${themeVariables.spacing.sm})) / ${CARD_COUNT});
  }
`;

/** So a card's own focus ring is not clipped by the scroll container. */
const ITEM_PADDING_BLOCK = '2px';

/**
 * The design's card height; there is no token. The skeleton reserves it while the first
 * page loads, and the items hold it as a floor once the reviews land: the card fills the
 * height it is given and reserves none itself, so without this a page of short reviews
 * would shrink the row as the skeleton gave way. A minimum, so a taller card still sets
 * the row and the others stretch to it.
 */
const CARD_HEIGHT = '273px';

export const item = css`
  ${cardWidth}
  min-height: calc(${CARD_HEIGHT} + 2 * ${ITEM_PADDING_BLOCK});
  scroll-snap-align: start;
  padding-block: ${ITEM_PADDING_BLOCK};
`;

/** Reserves the row's height while the first page loads. */
export const skeletonItem = css`
  ${cardWidth}
  height: ${CARD_HEIGHT};
  border-radius: var(${themeVariables.borderRadius.md.mobile});
  background-color: var(${themeVariables.colors.surface.disabled});
`;

export const message = css`
  margin: 0;
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});
  color: var(${themeVariables.colors.content.secondary});
`;
